import { useContext, useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { DashBoardPageUI } from './ui'
import { IDashBoardPageUI, TDashboardSource } from './types'

import { getOnlineOperators } from '@api/dashboard/api'
import { useLogin } from '@api/login/state'
import { useMonitoring } from '@api/dashboard/state'
import { UsersListType } from '@api/users/state.types'

import { useValue } from '@hooks/useValue'
import realtimeService from '@services/realtime.service'
import { reportUnexpectedClientError } from '@services/client-error.service'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'



/** Контейнер - Страница мониторинга */
export const DashBoardContainer = () => {
    const notify = useContext(NotificationContext)
    const operatorsIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const isOperatorsRequestRunningRef = useRef(false)
    const isOperatorsAccessDeniedRef = useRef(false)
    const [onlineOperators, setOnlineOperators] = useState<UsersListType[]>([])
    const [activityNow, setActivityNow] = useState(() => Date.now())
    const [isLoadingOnlineOperators, setIsLoadingOnlineOperators] = useState(false)
    
    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)
    const isOperatorsAccessDenied = useValue<boolean>(false)
    const dashboardSource = useValue<TDashboardSource>('modskill')

    // Хранилище страницы - Авторизация
    const { loginName } = useLogin(useShallow((state) => ({
        loginName: state.profile.loginName,
    })))
    const dashboardStorageKey = `dashboard_source_${loginName || 'default'}`

    useEffect(() => {
        const savedSource = localStorage.getItem(dashboardStorageKey) as TDashboardSource | null

        dashboardSource.setValue(savedSource === 'yaskill' ? 'yaskill' : 'modskill')
    }, [dashboardStorageKey]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem(dashboardStorageKey, dashboardSource.value)
    }, [dashboardStorageKey, dashboardSource.value]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleMonitoringAccessResponse = (res: { status: number; message: string }) => {
        if (res.status !== 200 && res.status !== 500) {
            if (res.status === 403) {
                clearStateMonitoring()
                isAccessDenied.setValue(true)
            } else {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        }
    }

    const loadOnlineOperators = (
        isShowLoading: boolean = true,
        isSystemRequest: boolean = !isShowLoading,
    ) => {
        if (isOperatorsRequestRunningRef.current || isOperatorsAccessDeniedRef.current) return

        isOperatorsRequestRunningRef.current = true
        setIsLoadingOnlineOperators(isShowLoading)

        getOnlineOperators(isSystemRequest).then((res) => {
            if (res.status === 200) {
                setOnlineOperators(res.data?.items || [])
                isOperatorsAccessDeniedRef.current = false
                isOperatorsAccessDenied.setValue(false)
                return
            }

            if (res.status === 403) {
                setOnlineOperators([])
                isOperatorsAccessDeniedRef.current = true
                isOperatorsAccessDenied.setValue(true)
                return
            }

            if (res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        }).catch((res) => {
            reportUnexpectedClientError(res, 'Dashboard.getOnlineOperators')
            notify && notifyOpen(res.message, 'error', 2000, notify)
        }).finally(() => {
            isOperatorsRequestRunningRef.current = false
            setIsLoadingOnlineOperators(false)
        })
    }

    useEffect(() => {
        const intervalID = setInterval(() => setActivityNow(Date.now()), 1000)

        return () => clearInterval(intervalID)
    }, [])

    useEffect(() => {
        const handlePageResume = () => {
            if (document.visibilityState === 'visible') {
                loadOnlineOperators(false, false)
            }
        }

        loadOnlineOperators()

        const unsubscribeOnlineOperators = realtimeService.subscribe('onlineOperators', () => {
            loadOnlineOperators(false)
        })

        operatorsIntervalRef.current = setInterval(() => {
            loadOnlineOperators(false)
        }, 60000)

        document.addEventListener('visibilitychange', handlePageResume)

        return () => {
            document.removeEventListener('visibilitychange', handlePageResume)
            unsubscribeOnlineOperators()

            if (operatorsIntervalRef.current) {
                clearInterval(operatorsIntervalRef.current)
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Хранилище страницы - Мониторинг
    const {
        clearStateMonitoring,
        actionGetStatsAppYearsModskill,
    } = useMonitoring(useShallow((state) => ({
        clearStateMonitoring: state.clearStateMonitoring,
        actionGetStatsAppYearsModskill: state.actionGetStatsAppYearsModskill,
    })))

    useEffect(() => {
        actionGetStatsAppYearsModskill().then((res) => {
            handleMonitoringAccessResponse(res)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps


    // ================================================================================
    // ================================================================================

    /** Возвращает имя месяца по его номеру */
    const getMonthName = (monthNumber: number): string => {
        const monthNames: string[] = [
            "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
            "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
        ]

        return monthNames[monthNumber - 1]
    }


    // ================================================================================

    /** Свойства передаваемые в UI */
    const propsToUI: IDashBoardPageUI = {
        isAccessDenied: isAccessDenied.value,
        dashboardSource,
        onlineOperators,
        activityNow,
        isLoadingOnlineOperators,
        isOperatorsAccessDenied: isOperatorsAccessDenied.value,
        getMonthName,
    }

    return <>
        <DashBoardPageUI {...propsToUI} />
    </>
}